import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import { Input, Switch } from 'antd5';
import { isFinite } from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveAttributeLayout } from 'components/damages/attributeLayout';
import {
  OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS,
  REALTIME_POTENTIAL_ATTRIBUTE_KEYS,
} from 'components/damages/constants';
import { renderColumn } from 'components/data/helpers';
import { Button, Tooltip } from 'components/ui';
import { TemperatureOutlined } from 'horizon/components/Images/ImageViewer/Icons/TemperatureIcon';
import { AtlasGqlDamageSchemaAttributeState } from 'types/atlas-graphql';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { editable } from 'utils/editable';
import { useFeatures } from 'utils/features';
import { useIsInView } from 'utils/hooks';
import { usePermissions } from 'utils/usePermissions';

import {
  AttributeColumn,
  AttributeFields,
  ComputedMetricInput,
  FixedButtonContainer,
  FormContainer,
  HelpTooltip,
  HintText,
  LabelActionButton,
  RevertComputedValueButton,
  StyledForm,
} from './AttributesForm.style';
import DamageEditAsset from './DamageEditAsset';
import DamageEditSchema from './DamageEditSchema';

const { TextArea } = Input;

const formItemLayout = {
  horizontal: {
    labelCol: {
      xs: { span: 12 },
    },
    wrapperCol: {
      xs: { span: 12 },
    },
  },
  vertical: {
    wrapperCol: {
      xs: { span: 24 },
    },
  },
};

function DisabledExtra({ name }) {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={t(
        'damage.attributeWillBeRemovedTooltip',
        '"{{name}}" will be removed after saving. Attribute is no longer in the schema',
        { name }
      )}
    >
      <div style={{ color: 'red', fontSize: 10, whiteSpace: 'normal' }}>
        {t('damage.attributeWillBeRemoved', '* "{{name}}" will be removed', { name })}
      </div>
    </Tooltip>
  );
}

function InvalidExtra() {
  const { t } = useTranslation();
  return (
    <div style={{ color: 'red', fontSize: 10, whiteSpace: 'normal' }}>
      {t('damage.invalidSelection', 'Invalid Selection')}
    </div>
  );
}

function stopPropagation(e) {
  e.stopPropagation();
}

export function AttributesForm({
  selections,
  saveButtonText,
  showComment = true,
  columns,
  onChange,
  loading,
  saving,
  saveDisabled,
  onSave,
  onCancel,
  formLayout,
  canChangeAsset = false,
  assetId,
  selectedAsset,
  assetSelectionScopeId,
  setAssetId,
  autoSelectAsset = true,
  canChangeSchema = false,
  schemaId,
  setSchemaId,
  canChangeCritical = false,
  critical,
  setCritical,
  includesRealtimePotentialAttrs,
  potentialAttrs,
  potentialAssets,
  backgroundColor,
  isImageViewer = false,
  onRequestDeltaTMeasurement,
  deltaTMeasurementEnabled = false,
  attributeLayout,
}) {
  const { t } = useTranslation();
  const submitLock = useRef(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const isScrolledToTop = useIsInView(topRef);
  const isScrolledToBottom = useIsInView(bottomRef);

  const [form] = Form.useForm();
  const [resetCounts, setResetCounts] = useState({});
  const resetVersions = useRef({});

  const { REQUIRE_VALID_DAMAGE } = useFeatures().features;
  const { canAccess } = usePermissions();
  const isEditable = canAccess([DAMAGE_EDIT_ATTRIBUTES]);

  const { columnCount: attributeColumnCount, columns: attributeColumnGroups } =
    resolveAttributeLayout(columns, attributeLayout);

  return (
    <FormContainer $isScrolledToTop={isScrolledToTop}>
      <StyledForm
        form={form}
        id="create-new-damage-attributes-form"
        layout={formLayout}
        onFinish={e => {
          if (!submitLock.current) {
            onSave(e);
          }
        }}
      >
        <div className="form-fields-container">
          <div ref={topRef} />
          {canChangeAsset && (
            <DamageEditAsset
              form={form}
              assetId={assetId}
              selectedAsset={selectedAsset}
              selectionScopeId={assetSelectionScopeId}
              onChange={setAssetId}
              autoSelectAsset={autoSelectAsset}
              potentialAssets={potentialAssets}
              externalLoading={loading}
            />
          )}
          {canChangeSchema && (
            <DamageEditSchema
              form={form}
              schemaId={schemaId}
              onChange={setSchemaId}
              externalLoading={loading}
            />
          )}
          {canChangeCritical && (
            <Form.Item
              {...formItemLayout[formLayout]}
              label={t('table.critical', 'Critical')}
              name="critical"
              initialValue={critical}
            >
              <Switch
                data-testid="critical-input"
                checked={critical}
                onChange={setCritical}
                disabled={!isEditable}
              />
            </Form.Item>
          )}
          <AttributeFields $columnCount={attributeColumnCount}>
            {attributeColumnGroups.map((attributeColumn, columnIndex) => (
              <AttributeColumn
                key={attributeColumn[0]?.name ?? columnIndex}
                $columnCount={attributeColumnCount}
                data-testid="attribute-column"
              >
                {attributeColumn.map(col => {
                  const { name, valid, state, disabled, description, unit, filters, type } = col;

                  const isRealtimeAttribute =
                    includesRealtimePotentialAttrs &&
                    REALTIME_POTENTIAL_ATTRIBUTE_KEYS.includes(name) &&
                    isFinite(potentialAttrs[name]);
                  const canOverrideComputedMetricAttribute =
                    OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS.includes(name);
                  const currentValue = selections[name];
                  const isManuallyUpdated =
                    isRealtimeAttribute &&
                    OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS.includes(name) &&
                    currentValue !== null &&
                    currentValue !== undefined &&
                    Number(currentValue) !== potentialAttrs[name];
                  const resetVersion = resetVersions.current[name] ?? 0;

                  const [min, max] = filters;
                  const tooltip = description && (
                    <HelpTooltip title={description}>
                      <QuestionCircleOutlined />
                    </HelpTooltip>
                  );
                  const input = renderColumn(
                    editable(col, {
                      onChange: selection => {
                        if (resetVersion === (resetVersions.current[name] ?? 0)) {
                          onChange(selection);
                        }
                      },
                      setSubmitLock:
                        type === 'number'
                          ? lock => {
                              submitLock.current = lock;
                            }
                          : undefined,
                    }),
                    {
                      attrs: selections,
                      disabled,
                      editable:
                        isEditable && (!isRealtimeAttribute || canOverrideComputedMetricAttribute),
                      REQUIRE_VALID_DAMAGE,
                      allowClear: true,
                      ...(col.unit ? { suffix: col.unit } : {}),
                      loading: loading && !Object.keys(selections).includes(name),
                    }
                  );
                  return (
                    <Form.Item
                      {...formItemLayout[formLayout]}
                      key={name}
                      label={
                        <span>
                          {tooltip}
                          {name}
                          {unit && <span style={{ paddingLeft: '0.2rem' }}>({unit})</span>}
                          {['Delta T', 'Delta T (ΔT)'].includes(name) &&
                            onRequestDeltaTMeasurement && (
                              <Tooltip
                                title={t(
                                  'damage.measureDeltaTTooltip',
                                  'Measure Delta T from a reference temperature box'
                                )}
                              >
                                <LabelActionButton
                                  type="button"
                                  onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onRequestDeltaTMeasurement();
                                  }}
                                  disabled={!deltaTMeasurementEnabled}
                                >
                                  <TemperatureOutlined />
                                </LabelActionButton>
                              </Tooltip>
                            )}
                        </span>
                      }
                      extra={
                        !valid && !loading ? (
                          <InvalidExtra />
                        ) : state === AtlasGqlDamageSchemaAttributeState.Disabled && !loading ? (
                          <DisabledExtra name={name} />
                        ) : null
                      }
                    >
                      {isRealtimeAttribute &&
                      canOverrideComputedMetricAttribute &&
                      isEditable &&
                      !disabled ? (
                        <ComputedMetricInput
                          key={`${name}-${resetCounts[name] ?? 0}-${String(potentialAttrs[name])}`}
                        >
                          {input}
                        </ComputedMetricInput>
                      ) : (
                        input
                      )}
                      <HintText>
                        {isRealtimeAttribute &&
                          (isManuallyUpdated ? (
                            <>
                              {t(
                                'damage.resetDifferentComputedValue',
                                'This value differs from the bounding box calculation — click to reset it.'
                              )}
                              <Tooltip
                                title={t(
                                  'damage.revertToBoundingBoxValue',
                                  'Revert to bounding box value'
                                )}
                              >
                                <RevertComputedValueButton
                                  type="button"
                                  aria-label={t(
                                    'damage.revertToBoundingBoxValue',
                                    'Revert to bounding box value'
                                  )}
                                  onMouseDown={event => event.preventDefault()}
                                  onClick={() => {
                                    resetVersions.current[name] = resetVersion + 1;
                                    onChange({ name, number: potentialAttrs[name] });
                                    setResetCounts(current => ({
                                      ...current,
                                      [name]: (current[name] ?? 0) + 1,
                                    }));
                                  }}
                                >
                                  <ReloadOutlined aria-hidden={true} />
                                </RevertComputedValueButton>
                              </Tooltip>
                            </>
                          ) : (
                            t(
                              'damage.valueCalculatedByBoundingBox',
                              'This value is calculated by the bounding box'
                            )
                          ))}
                        {!isRealtimeAttribute &&
                          type === 'number' &&
                          `${t('damage.range', 'Range:')} ${!!Number(min?.value) ? min.value : 0} ${
                            !!Number(max?.value)
                              ? t('damage.rangeTo', 'to {{max}}', { max: max.value })
                              : t('damage.andUp', 'and up')
                          }`}
                      </HintText>
                    </Form.Item>
                  );
                })}
              </AttributeColumn>
            ))}
          </AttributeFields>
          {showComment && (
            <Form.Item
              {...formItemLayout[formLayout]}
              label={t('comments.comment', 'Comment')}
              name="comment"
            >
              <TextArea
                id="comment-text-input"
                data-testid="comment-text-input"
                disabled={!isEditable}
                rows={2}
                onKeyDown={stopPropagation}
                onKeyUp={stopPropagation}
              />
            </Form.Item>
          )}
          <div ref={bottomRef} />
        </div>
        {onSave && (
          <FixedButtonContainer
            $isScrolledToBottom={isScrolledToBottom}
            $backgroundColor={backgroundColor}
            $isImageViewer={isImageViewer}
          >
            <Button data-testid="damage-cancelBtn" onClick={onCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              data-testid="damage-saveBtn"
              disabled={saveDisabled}
              htmlType="submit"
              loading={saving}
              type="primary"
            >
              {saveButtonText}
            </Button>
          </FixedButtonContainer>
        )}
      </StyledForm>
    </FormContainer>
  );
}
